import { PENDING_FIND_RIDE_SEARCH_KEY } from "../constants/findRide.constants";
import type { FindRideSearchCriteria } from "../types/findRide.types";

/** What to do after login / on Find Ride mount when pending criteria exists. */
export type PendingFindRideIntent = "search" | "notify";

export type PendingFindRidePayload = {
  criteria: FindRideSearchCriteria;
  intent: PendingFindRideIntent;
};

function isValidCriteria(
  parsed: Partial<FindRideSearchCriteria> | null | undefined
): parsed is FindRideSearchCriteria {
  return Boolean(
    parsed?.pickup?.placeId && parsed?.drop?.placeId && parsed?.date
  );
}

export function savePendingFindRideSearch(
  criteria: FindRideSearchCriteria,
  intent: PendingFindRideIntent = "search"
): void {
  const payload: PendingFindRidePayload = { criteria, intent };
  sessionStorage.setItem(PENDING_FIND_RIDE_SEARCH_KEY, JSON.stringify(payload));
}

/**
 * Reads pending payload. Supports legacy shape (criteria-only JSON) as intent "search".
 */
export function readPendingFindRideSearch(): PendingFindRidePayload | null {
  try {
    const raw = sessionStorage.getItem(PENDING_FIND_RIDE_SEARCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as
      | PendingFindRidePayload
      | FindRideSearchCriteria;

    if ("criteria" in parsed && isValidCriteria(parsed.criteria)) {
      return {
        criteria: parsed.criteria,
        intent: parsed.intent === "notify" ? "notify" : "search",
      };
    }

    if (isValidCriteria(parsed as FindRideSearchCriteria)) {
      return { criteria: parsed as FindRideSearchCriteria, intent: "search" };
    }

    return null;
  } catch {
    return null;
  }
}

/** Criteria only — for form prefill. */
export function readPendingFindRideCriteria(): FindRideSearchCriteria | null {
  return readPendingFindRideSearch()?.criteria ?? null;
}

export function clearPendingFindRideSearch(): void {
  sessionStorage.removeItem(PENDING_FIND_RIDE_SEARCH_KEY);
}
